import { IonImg } from '@ionic/react';
import { useMemo } from 'react';
import { useIntl } from 'react-intl';

import ExpandableSection from '@components/animations/ExpandableSection';
import ClientAvatar, { Color } from '@components/ClientAvatar';
import TimeStamp from '@components/dates/TimeStamp';
import ExpertAvatar from '@components/ExpertAvatar';
import FileResource from '@components/FileResource';
import HoverText from '@components/HoverText';
import { MessageSearch } from '@components/messaging/Messages';
import styles from '@components/messaging/Messages.module.css';
import PinToggle from '@components/PinToggle';
import TextWithIcon from '@components/text/TextWithIcon';
import BodyCopy from '@components/typography/BodyCopy';
import Hyperlink from '@components/typography/Hyperlink';
import useMe from '@hooks/useMe';
import useMyExpert from '@hooks/useMyExpert';
import useOpener from '@hooks/useOpener';
import useSanitizeHtml from '@hooks/useSanitizeHtml';
import { MessageFragment as MessageType, PinResourceTypeEnum } from '@typing/Generated';
import { imageUrl } from '@utils/imageUtils';
import { journeyRoute } from '@utils/routeUtils';

type Props = {
  authorColor: Color | undefined;
  authorOfPreviousMessage: boolean;
  journeyId: string;
  message: MessageType;
  messageSearch: MessageSearch;
};

const Message = ({ authorColor, authorOfPreviousMessage, journeyId, message, messageSearch }: Props) => {
  const me = useMe();
  const myExpert = useMyExpert();
  const userIsSender = me?.id === message.author.id && !message.fromGrayce;
  const intl = useIntl();
  const dateOpener = useOpener();

  const resourceLink = useMemo(() => {
    if (message.record) {
      return journeyRoute({
        journeyId,
        linkAfterJourney:
          message.record.__typename === 'Resource'
            ? `/client-visible-tasks/${message.record.task.id}`
            : `/worksheets/${message.record.id}`,
        myExpert
      });
    }
  }, [journeyId, message.record, myExpert]);

  const recordCopy = useMemo(() => {
    if (message.record?.__typename === 'Worksheet') {
      return intl.formatMessage({ id: 'forms.messageCreate.worksheetInfo' }, { title: message.record.title });
    } else if (message.record?.task.category.other) {
      return intl.formatMessage(
        { id: 'forms.messageCreate.resourceInfo' },
        { category: message.record.task.category.other, title: message.record.title }
      );
    } else if (message.record?.task.category.categoryOption) {
      return intl.formatMessage(
        { id: 'forms.messageCreate.resourceInfo' },
        {
          category: intl.formatMessage({
            id: `models.tasks.categoryOptions.${message.record.task.category.categoryOption}`
          }),
          title: message.record.title
        }
      );
    }
    return intl.formatMessage(
      { id: 'forms.messageCreate.resourceInfoNoCategory' },
      {
        title: message.record?.title
      }
    );
  }, [intl, message.record]);

  const avatar = useMemo(() => {
    if (message.fromGrayce) {
      return (
        <HoverText content={intl.formatMessage({ id: 'dictionary.fromGrayce' })}>
          <IonImg
            alt={intl.formatMessage({ id: 'dictionary.fromGrayce' })}
            className={styles.messageImg}
            src={imageUrl('logoIcon')}
          />
        </HoverText>
      );
    } else if (message.author.__typename === 'Expert') {
      if (message.author.photoUrl) {
        return <ExpertAvatar expert={message.author} size={30} />;
      } else {
        return (
          <div className={styles.scanning} data-test="scanning">
            <TextWithIcon icon="spinner" iconFirst text={intl.formatMessage({ id: 'dictionary.scanning' })} />
          </div>
        );
      }
    } else if (message.author.__typename === 'ClientDeletion') {
      return (
        <ClientAvatar
          clientId={null}
          color="neutral100"
          firstName={intl.formatMessage({ id: 'models.clientDeletion.firstName' })}
          fontSize={14}
          journeyId={null}
          lastName={intl.formatMessage({ id: 'models.clientDeletion.lastName' })}
          size={30}
        />
      );
    } else {
      return (
        <ClientAvatar
          clientId={message.author.id}
          color={authorColor ?? 'primary500'}
          firstName={message.author.firstName}
          fontSize={14}
          journeyId={journeyId}
          lastName={message.author.lastName}
          size={30}
        />
      );
    }
  }, [authorColor, intl, journeyId, message.author, message.fromGrayce]);

  const highlightedBodyText = useMemo(() => {
    if (messageSearch.searchTerm && message.body) {
      const regex = new RegExp(`(${messageSearch.searchTerm})(?![^<>]*>)`, 'gi');
      const parts = message.body.split(regex);
      return parts
        .map(
          part =>
            `<span class=${
              part.toLowerCase() === messageSearch.searchTerm?.toLowerCase() ? styles.highlight : ''
            }>${part}</span>`
        )
        .join('');
    }
    return message.body;
  }, [message.body, messageSearch.searchTerm]);

  const sanitizedBody = useSanitizeHtml(highlightedBodyText, journeyId, message);

  return (
    <div
      className={`${userIsSender ? styles.messageReverse : styles.message} ${
        authorOfPreviousMessage ? styles.smallSpacing : ''
      }`}
      data-test="message"
      onClick={dateOpener.toggle}
    >
      <div>
        <div className={styles.messageAvatarSpacing}>
          {authorOfPreviousMessage ? null : (
            <div>
              <div className={styles.messageAvatar}>{avatar}</div>
            </div>
          )}
        </div>
      </div>
      <div>
        {authorOfPreviousMessage ? (
          <ExpandableSection isOpen={dateOpener.isOpen}>
            <div className={styles.messageDate}>
              <BodyCopy color="neutral500">
                <TimeStamp value={message.createdAt} />
              </BodyCopy>
            </div>
          </ExpandableSection>
        ) : (
          <div className={styles.messageDate}>
            <BodyCopy color="neutral500">
              <TimeStamp value={message.createdAt} />
            </BodyCopy>
          </div>
        )}

        <div
          className={`${userIsSender ? styles.messageOutgoing : styles.messageIncoming} ${
            authorOfPreviousMessage ? styles.noMargin : ''
          }`}
        >
          <div className={styles.body}>
            {message.record && (
              <Hyperlink className={styles.messageResource} routerLink={resourceLink}>
                {recordCopy}
              </Hyperlink>
            )}
            {sanitizedBody}
          </div>
        </div>
        {message.fileResources.length > 0 &&
          message.fileResources.map(fileResource => (
            <div className={styles.pinAndFile} key={fileResource.id}>
              {userIsSender && (
                <div className={styles.pin}>
                  <PinToggle
                    color="neutral500"
                    journeyId={journeyId}
                    resourceId={fileResource.id}
                    resourceType={PinResourceTypeEnum.FILERESOURCE}
                  />
                </div>
              )}
              <div className={`${userIsSender ? styles.messageOutgoing : styles.messageIncoming}`}>
                <FileResource fileResource={fileResource} textColor={userIsSender ? 'shadesWhite' : undefined} />
              </div>
              {!userIsSender && (
                <div className={styles.pin}>
                  <PinToggle
                    color="neutral500"
                    journeyId={journeyId}
                    resourceId={fileResource.id}
                    resourceType={PinResourceTypeEnum.FILERESOURCE}
                  />
                </div>
              )}
            </div>
          ))}
      </div>
    </div>
  );
};
export default Message;
